import { DateTime } from "luxon";
import classNames from "classnames";
import { useForm } from "react-hook-form";
import { useNotifier } from "react-headless-notifier";
import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Table,
  Notice,
  Select,
  Button,
  Checkbox,
  PlusIcon,
  MapPinIcon,
  IconButton,
  NotifierNotification,
  SelectOption,
  ConfirmationModal,
  UnsavedChangesPrompt,
} from "@mwi/ui";
import SubPeriods from "./sections/SubPeriods";
import Exceptions from "./sections/Exceptions";
import useErrorHandler from "hooks/useErrorHandler";
import { ScheduleWeekDay } from "types/sites/schedule";
import {
  useGetSiteScheduleQuery,
  useGetSitesQuery,
  useUpdateSiteScheduleMutation,
} from "api";
import useTitle from "hooks/useTitle";

type Inputs = {
  vet_site: string;
  week_days: ScheduleWeekDay[];
  exceptions: {
    id?: number;
    is_open: string;
    day: DateTime;
    start_time: string;
    end_time: string;
  }[];
};

const PracticeAppointmentsPage = (): JSX.Element => {
  useTitle("Appointments");

  const errorHandler = useErrorHandler();
  const { notify } = useNotifier();

  const [openConfirmationModal, setOpenConfirmationModal] =
    useState<boolean>(false);

  const {
    watch,
    reset,
    control,
    setError,
    setValue,
    getValues,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm<Inputs>({
    defaultValues: {
      vet_site: "all_sites",
    },
  });

  const { data: sites } = useGetSitesQuery();

  const { data: siteSchedule, isFetching: fetchingSiteSchedule } =
    useGetSiteScheduleQuery({
      locationUuid:
        watch("vet_site") !== "all_sites" ? getValues("vet_site") : undefined,
    });

  const [
    update,
    { isLoading: isUpdating, isSuccess: didUpdate, error: updateError },
  ] = useUpdateSiteScheduleMutation();

  const handleAddSubPeriod = (weekDayIdx: number) => {
    setValue(`week_days.${weekDayIdx}.periods`, [
      ...getValues(`week_days.${weekDayIdx}.periods`),
      { opening_time: null, closing_time: null },
    ]);
  };

  const handleUpdateSiteSchedule = () => {
    if (watch("vet_site") === "all_sites" && !openConfirmationModal) {
      setOpenConfirmationModal(true);
      return;
    }

    setOpenConfirmationModal(false);

    clearErrors();
    update({
      week_days: getValues("week_days"),
      exceptions: getValues("exceptions").map((exp) => ({
        id: exp.id,
        is_open: exp.is_open === "open" ? true : false,
        day: exp.day.toISO() ?? undefined,
        start_time: DateTime.fromISO(exp.start_time!).isValid
          ? exp.start_time
          : undefined,
        end_time: DateTime.fromISO(exp.end_time!).isValid
          ? exp.end_time
          : undefined,
      })),
      location_uuid:
        getValues("vet_site") !== "all_sites"
          ? getValues("vet_site")
          : undefined,
    });
  };

  useEffect(() => {
    if (siteSchedule) {
      reset({
        vet_site: getValues("vet_site"),
        week_days: siteSchedule.week_days.map((sa) => ({
          ...sa,
          periods: sa.periods.map((p) => ({ ...p })),
        })),
        exceptions: siteSchedule.exceptions.map((exp) => ({
          ...exp,
          id: exp.id,
          is_open: exp.is_open ? "open" : "close",
          day: DateTime.fromISO(exp.day!).isValid
            ? DateTime.fromISO(exp.day!)
            : "",
        })),
      });
    }
  }, [siteSchedule]);

  useEffect(() => {
    if (didUpdate) {
      const site = sites?.find((s) => s.id === getValues("vet_site"));

      notify(
        <NotifierNotification
          title="Site Schedule Updated"
          message={`${site ? `${site.name}'s` : "All sites"} appointment opening times and exceptions have been updated`}
        />,
      );
    }

    if (updateError) {
      errorHandler(updateError, setError);
    }
  }, [didUpdate, updateError]);

  const timeOptions = useMemo(() => {
    let startTime = DateTime.now().startOf("day");
    const endTime = DateTime.now().endOf("day");

    const intervals: string[] = [];
    while (endTime > startTime) {
      intervals.push(startTime.toFormat("T"));
      startTime = startTime.plus({ minutes: 15 });
    }

    return intervals;
  }, []);

  const siteOptions = useMemo(() => {
    const allSitesOption = { id: "all_sites", name: "All sites" };
    return sites ? [allSitesOption, ...sites] : [allSitesOption];
  }, [sites]);

  return (
    <UnsavedChangesPrompt isDirty={isDirty}>
      <div className="grid tablet:grid-cols-8 desktop:grid-cols-10 !pb-16 gap-x-6">
        {watch("vet_site") === "all_sites" && (
          <div className="col-span-7 col-start-1 desktop:col-span-6">
            <Notice className="mb-10">
              Saving these opening & closing times and their associated
              exceptions will override all vet sites to this configuration. Any
              custom configurations across vet sites will be lost.
            </Notice>
          </div>
        )}

        <div className="flex flex-col gap-y-[2px] col-span-full mb-14 text-lg">
          <p className="font-semibold">
            Manage the hours when customers can book appointments
          </p>
          <p>
            Pet owners will only be shown timeslots found within these hours
          </p>
        </div>

        <div className="col-span-3 col-start-1 mb-8">
          <Select
            name="vet_site"
            size="medium"
            control={control}
            prefixIcon={<MapPinIcon />}
            placeholder="Select vet site"
          >
            {siteOptions?.map((s) => (
              <SelectOption key={s.id} value={s.id}>
                {s.name}
              </SelectOption>
            ))}
          </Select>
        </div>

        <div className="col-span-6 col-start-1 mb-16 desktop:col-span-8">
          <Table loading={fetchingSiteSchedule}>
            <Table.Head>
              <Table.Row>
                <Table.Item as="th">Open</Table.Item>
                <Table.Item as="th">Day</Table.Item>
                <Table.Item as="th" className="w-[256px]">
                  From
                </Table.Item>
                <Table.Item as="th" className="w-[256px]">
                  Until
                </Table.Item>
                <Table.Item as="th">Add</Table.Item>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {watch("week_days")
                ? watch("week_days").map((weekDay, weekDayIdx) => {
                    const openingTime = DateTime.fromISO(
                      watch(`week_days.${weekDayIdx}.periods.0.opening_time`)!,
                    );
                    const closingTime = DateTime.fromISO(
                      watch(`week_days.${weekDayIdx}.periods.0.closing_time`)!,
                    );

                    let ctOptions = timeOptions;
                    if (openingTime?.isValid) {
                      ctOptions = timeOptions.filter(
                        (ct) => ct > openingTime.toFormat("T"),
                      );
                    }

                    if (
                      openingTime?.isValid &&
                      closingTime?.isValid &&
                      openingTime >= closingTime
                    ) {
                      setTimeout(() => {
                        setValue(
                          `week_days.${weekDayIdx}.periods.0.closing_time`,
                          null,
                        );
                        if (weekDay.periods.length > 0) {
                          setValue(`week_days.${weekDayIdx}.periods`, [
                            ...weekDay.periods.slice(0, 1),
                          ]);
                        }
                      }, 0);
                    }

                    return (
                      <Fragment key={weekDay.week_day}>
                        <Table.Row
                          className={classNames("", {
                            "border-b border-grey-light":
                              weekDay.periods?.length === 1,
                            "bg-grey-lightest": !weekDay.enabled,
                          })}
                        >
                          <Table.Item className="!py-2">
                            <Checkbox
                              control={control}
                              name={`week_days.${weekDayIdx}.enabled`}
                              className={classNames("!rounded-sm !w-5 !h-5", {
                                "!border-grey-light border-[2px] focus:shadow-none":
                                  !weekDay.enabled,
                              })}
                            />
                          </Table.Item>
                          <Table.Item className="py-2">
                            <span
                              className={classNames(
                                "text-base font-semibold capitalize text-black select-none",
                                {
                                  "text-grey-dark": !weekDay.enabled,
                                },
                              )}
                            >
                              {weekDay.week_day}
                            </span>
                          </Table.Item>
                          <Table.Item className="relative !py-2 pr-8">
                            <Select
                              size="small"
                              control={control}
                              placeholder="Select"
                              disabled={!weekDay.enabled}
                              name={`week_days.${weekDayIdx}.periods.0.opening_time`}
                              buttonClassName={classNames(
                                "!px-4 !rounded",
                                "text-base",
                                "shadow-none !bg-grey-lightest !border-grey-lightest",
                                "!focus:border !focus:border-primary",
                                {
                                  "!border-error-light":
                                    errors?.week_days?.[weekDayIdx]
                                      ?.periods?.[0]?.opening_time,
                                },
                              )}
                            >
                              {timeOptions &&
                                timeOptions.map((s, idx) => (
                                  <SelectOption key={idx} value={s}>
                                    {s}
                                  </SelectOption>
                                ))}
                            </Select>

                            <div className="absolute inset-y-1/2 right-[-3px] w-[7px] h-[2px] bg-black"></div>
                          </Table.Item>
                          <Table.Item className="!py-2 pl-8">
                            <Select
                              size="small"
                              control={control}
                              placeholder="Select"
                              disabled={!weekDay.enabled}
                              name={`week_days.${weekDayIdx}.periods.0.closing_time`}
                              buttonClassName={classNames(
                                "!px-4 !rounded",
                                "text-base",
                                "shadow-none !bg-grey-lightest !border-grey-lightest",
                                "!focus:border !focus:border-primary",
                                {
                                  "!border-error-light":
                                    errors?.week_days?.[weekDayIdx]
                                      ?.periods?.[0]?.closing_time,
                                },
                              )}
                            >
                              {ctOptions &&
                                ctOptions.map((s, idx) => (
                                  <SelectOption key={idx} value={s}>
                                    {s}
                                  </SelectOption>
                                ))}
                            </Select>
                          </Table.Item>
                          <Table.Item className="!py-2">
                            <IconButton
                              onClick={() => handleAddSubPeriod(weekDayIdx)}
                              disabled={
                                !weekDay.enabled ||
                                !watch(
                                  `week_days.${weekDayIdx}.periods.${weekDay.periods?.length - 1}.opening_time`,
                                ) ||
                                !watch(
                                  `week_days.${weekDayIdx}.periods.${weekDay.periods?.length - 1}.closing_time`,
                                )
                              }
                            >
                              <PlusIcon className="w-4 h-4" />
                            </IconButton>
                          </Table.Item>
                        </Table.Row>

                        <SubPeriods
                          weekDayIdx={weekDayIdx}
                          timeOptions={timeOptions}
                          disabled={!weekDay.enabled}
                          {...{ watch, setValue, control, errors }} // RHF
                        />
                      </Fragment>
                    );
                  })
                : undefined}
            </Table.Body>
          </Table>
        </div>

        <div className="mb-10 col-span-full">
          <p className="font-semibold">Add exceptions to booking hours</p>
          <p>
            Block the ability to book appointments during holidays or other
            special dates
          </p>
        </div>

        <div className="col-start-1 mb-16 tablet:col-span-7 desktop:col-span-9">
          <Exceptions
            timeOptions={timeOptions}
            loading={fetchingSiteSchedule}
            {...{ control, watch, setValue, errors, getValues }} // RHF
          />
        </div>

        <div className="col-span-full max-w-[185px]">
          <Button
            block
            loading={isUpdating}
            disabled={fetchingSiteSchedule}
            onClick={handleUpdateSiteSchedule}
          >
            Save changes
          </Button>
        </div>

        <ConfirmationModal
          title="Are you sure?"
          description="
          You're about to override any custom opening time configurations and related exceptions to this configuration.
          This action cannot be undone once confirmed.
        "
          isOpen={openConfirmationModal}
          onConfirm={handleUpdateSiteSchedule}
          onClose={() => setOpenConfirmationModal(false)}
        />
      </div>
    </UnsavedChangesPrompt>
  );
};

export default PracticeAppointmentsPage;
